import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command, CommandInput, CommandList, CommandGroup, CommandItem,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const contactName = (c) =>
  (c.company_name && c.company_name.trim())
    ? c.company_name
    : `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || c.phone || c.id;

const contactPersonName = (c) => `${c.first_name || ''} ${c.last_name || ''}`.trim();

const userName = (u) => u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim();

/**
 * Beneficiary picker for Expenses. Searches across internal Users AND
 * Contacts (vendors/subcontractors) as you type, and also lets you type a
 * free-text name for an external beneficiary that isn't in the system yet
 * (e.g. "Summers Irrigation") — saved as beneficiary_type="other".
 *
 * When a Contact has a `company_name`, the COMPANY takes precedence as the
 * beneficiary (Alian: "La compañía toma precedencia por encima del contact
 * person... le debo a Summers Irrigation, no a Jimmy") — `onChange` receives
 * a 4th argument with the individual contact person's name so the caller can
 * still show "Contacto: Jimmy Somers" underneath without losing that detail.
 */
export function BeneficiaryCombobox({
  users = [],
  contacts = [],
  type = 'other',
  id = '',
  name = '',
  onChange,
  placeholder = 'Search or type a beneficiary...',
  testId = 'expense-beneficiary-combobox',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const displayName = type === 'other'
    ? name
    : type === 'user'
      ? userName(users.find((u) => u.id === id) || {})
      : type === 'contact'
        ? contactName(contacts.find((c) => c.id === id) || {})
        : '';

  const trimmed = search.trim();
  const q = trimmed.toLowerCase();

  const filteredUsers = useMemo(
    () => (!q ? users : users.filter((u) => userName(u).toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q))),
    [users, q],
  );
  const filteredContacts = useMemo(
    () => (!q ? contacts : contacts.filter((c) =>
      contactName(c).toLowerCase().includes(q) || contactPersonName(c).toLowerCase().includes(q)
    )),
    [contacts, q],
  );
  const exactMatch = q && (
    filteredUsers.some((u) => userName(u).toLowerCase() === q)
    || filteredContacts.some((c) => contactName(c).toLowerCase() === q)
  );

  const select = (t, i, n, personName = '') => { onChange(t, i, n, personName); setOpen(false); setSearch(''); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="w-full h-10 justify-between font-normal"
        >
          {displayName ? (
            <span className="truncate flex items-center gap-1.5">
              <span className="text-xs">{type === 'user' ? '👤' : type === 'contact' ? '🏢' : '✎'}</span>
              {displayName}
            </span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Type a name (user, vendor, or new)..."
            data-testid={`${testId}-input`}
          />
          <CommandList>
            {trimmed && !exactMatch && (
              <CommandGroup heading="New beneficiary">
                <CommandItem
                  value={`new-${trimmed}`}
                  data-testid={`${testId}-free-text-option`}
                  onSelect={() => select('other', '', trimmed)}
                >
                  <UserPlus className="mr-2 h-4 w-4 text-[#003366]" />
                  Use "{trimmed}" as beneficiary
                </CommandItem>
              </CommandGroup>
            )}
            <CommandItem
              value="__none__"
              data-testid={`${testId}-option-none`}
              onSelect={() => select('other', '', '')}
            >
              <Check className={cn('mr-2 h-4 w-4', !id && !name ? 'opacity-100' : 'opacity-0')} />
              <span className="text-gray-400">No beneficiary</span>
            </CommandItem>
            {filteredUsers.length > 0 && (
              <CommandGroup heading="Workers">
                {filteredUsers.map((u) => (
                  <CommandItem
                    key={`u-${u.id}`}
                    value={`user-${u.id}`}
                    data-testid={`${testId}-option-user-${u.id}`}
                    onSelect={() => select('user', u.id, userName(u))}
                  >
                    <Check className={cn('mr-2 h-4 w-4', type === 'user' && id === u.id ? 'opacity-100' : 'opacity-0')} />
                    <span>👤 {userName(u)}</span>
                    {u.role && <span className="ml-2 text-xs text-gray-400">({u.role})</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {filteredContacts.length > 0 && (
              <CommandGroup heading="Vendors / Contacts">
                {filteredContacts.slice(0, 300).map((c) => (
                  <CommandItem
                    key={`c-${c.id}`}
                    value={`contact-${c.id}`}
                    data-testid={`${testId}-option-contact-${c.id}`}
                    onSelect={() => select('contact', c.id, contactName(c), contactPersonName(c))}
                  >
                    <Check className={cn('mr-2 h-4 w-4', type === 'contact' && id === c.id ? 'opacity-100' : 'opacity-0')} />
                    <div className="flex flex-col">
                      <span>🏢 {contactName(c)}</span>
                      {contactPersonName(c) && contactPersonName(c) !== contactName(c) && (
                        <span className="text-xs text-gray-400 pl-5">{contactPersonName(c)}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {trimmed && filteredUsers.length === 0 && filteredContacts.length === 0 && (
              <div className="py-3 px-2 text-center text-xs text-gray-400">
                No matches — use the option above to add "{trimmed}" as a new beneficiary.
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default BeneficiaryCombobox;
